// Web shim for @react-native-async-storage/async-storage.
// Backed by window.localStorage. The upstream web build pulls in `merge-options`,
// whose mixed CJS/ESM interop breaks under webpack's harmony-module handling
// ("require is not defined"). A direct localStorage implementation avoids that.

const store = () => (typeof window !== 'undefined' ? window.localStorage : null);

const AsyncStorage = {
  async getItem(key) {
    const ls = store();
    return ls ? ls.getItem(key) : null;
  },

  async setItem(key, value) {
    const ls = store();
    if (ls) ls.setItem(key, value);
  },

  async removeItem(key) {
    const ls = store();
    if (ls) ls.removeItem(key);
  },

  async mergeItem(key, value) {
    const ls = store();
    if (!ls) return;
    const existing = ls.getItem(key);
    if (!existing) {
      ls.setItem(key, value);
      return;
    }
    try {
      const merged = { ...JSON.parse(existing), ...JSON.parse(value) };
      ls.setItem(key, JSON.stringify(merged));
    } catch {
      ls.setItem(key, value);
    }
  },

  async clear() {
    const ls = store();
    if (ls) ls.clear();
  },

  async getAllKeys() {
    const ls = store();
    if (!ls) return [];
    return Object.keys(ls);
  },

  async multiGet(keys) {
    const ls = store();
    return keys.map(key => [key, ls ? ls.getItem(key) : null]);
  },

  async multiSet(keyValuePairs) {
    const ls = store();
    if (!ls) return;
    keyValuePairs.forEach(([key, value]) => ls.setItem(key, value));
  },

  async multiRemove(keys) {
    const ls = store();
    if (!ls) return;
    keys.forEach(key => ls.removeItem(key));
  },

  async multiMerge(keyValuePairs) {
    for (const [key, value] of keyValuePairs) {
      await this.mergeItem(key, value);
    }
  },
};

export function useAsyncStorage(key) {
  return {
    getItem: (...args) => AsyncStorage.getItem(key, ...args),
    setItem: (value, ...args) => AsyncStorage.setItem(key, value, ...args),
    mergeItem: (value, ...args) => AsyncStorage.mergeItem(key, value, ...args),
    removeItem: (...args) => AsyncStorage.removeItem(key, ...args),
  };
}

export default AsyncStorage;
